import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { HubConnectionState } from "@microsoft/signalr";
import { useAccessToken } from "@/auth/useAccessToken";
import { SharedHub, authedHub, setHubAccessToken } from "./sharedHub";

const hubBase = import.meta.env.VITE_CONNECT_BFF_BASE_URL ?? "http://localhost:5104";
const meetingsUrl = `${hubBase}/hubs/meetings`;

/**
 * The page's meetings-hub connections — one signed-in, one anonymous (guests). Shared by the room, its
 * whiteboard and notes, with stateful reconnect (a blip keeps the same connection id) and a grace period
 * so a remount of the room reuses the connection instead of dropping out of the meeting.
 */
const userMeetingsHub = authedHub(meetingsUrl, { stateful: true, graceMs: 8000 });
const guestMeetingsHub = new SharedHub({ url: meetingsUrl, stateful: true, graceMs: 8000 });

// Names must match MeetingsHubEvents on the server.
export const MeetingsEvents = {
    UserJoined: "meeting.user.joined",
    UserLeft: "meeting.user.left",
    Participants: "meeting.participants",
    ReceiveOffer: "meeting.rtc.offer",
    ReceiveAnswer: "meeting.rtc.answer",
    ReceiveIceCandidate: "meeting.rtc.ice",
    MediaToggled: "meeting.media.toggled",
    HandRaised: "meeting.hand.raised",
    ScreenShare: "meeting.screen.share",
    Reaction: "meeting.reaction",
    Caption: "meeting.caption",
    MeetingChat: "meeting.chat",
    /** A stored chat message was edited, deleted or reacted to. */
    MeetingChatChanged: "meeting.chat.changed",
    PresenterChanged: "meeting.presenter.changed",
    ForceMuted: "meeting.force.muted",
    SpotlightChanged: "meeting.spotlight.changed",
    LayoutSynced: "meeting.layout.synced",
    RecordingState: "meeting.recording.state",
    /** The host's recording was saved to Drive and registered on the meeting. */
    RecordingSaved: "meeting.recording.saved",
    MeetingEnded: "meeting.ended",
    // Lobby events
    LobbyKnock: "meeting.lobby.knock",
    /** The host locked or unlocked the meeting (or you were turned away because it is locked). */
    MeetingLocked: "meeting.locked",
    LobbyAdmitted: "meeting.lobby.admitted",
    LobbyDenied: "meeting.lobby.denied",
    /** The server put this connection in the lobby — wait to be admitted. */
    LobbyWaiting: "meeting.lobby.waiting",
    /** Someone left the lobby (admitted, denied or gone) — drop their row. */
    LobbyLeft: "meeting.lobby.left",
    /** An invitee declined a ringing call. */
    CallDeclined: "meeting.call.declined",
    // Whiteboard + meeting notes (MeetingsHub.Collab.cs)
    /** Board ops from someone else (to people with the board open). */
    WhiteboardOps: "meeting.whiteboard.ops",
    /** Someone's in-progress stroke/shape, or null to clear it. */
    WhiteboardPreview: "meeting.whiteboard.preview",
    /** The leader's viewport (centre + zoom), for followers. */
    WhiteboardView: "meeting.whiteboard.view",
    /** Board opened/closed, locked, cleared or leader changed — to the whole room. */
    WhiteboardState: "meeting.whiteboard.state",
    /** A meeting-notes block was created, edited or deleted. */
    NotesChanged: "meeting.notes.changed",
    /** Someone started/stopped editing a notes block. */
    NotesEditing: "meeting.notes.editing",
    // Speaker-rotated video (MeetingsHub.VideoSlots.cs)
    /** Who may send video now: { active: connectionId[] (tile order), capacity }. */
    VideoSlots: "meeting.video.slots"
} as const;

export type MeetingsEventName = (typeof MeetingsEvents)[keyof typeof MeetingsEvents];
type EventMap = Partial<Record<MeetingsEventName, (payload: unknown) => void>>;

export interface WhiteboardOpenResult {
    board: import("./connectApi").WhiteboardSnapshot;
    canDraw: boolean;
    isHost: boolean;
    /** Connections allowed to draw while the board is locked. */
    drawers: string[];
}

export interface WhiteboardOpsResult {
    ok: boolean;
    revision: number;
    rejected: string[];
    /** "invalid" | "full" | "rate" | "locked" | "not-open" | "gone" */
    reason?: string | null;
}

export interface NoteLock {
    blockId: string;
    userId: string;
    displayName: string;
    connectionId: string;
}

/** Offer/answer/ICE as sent through the hub: the browser's object plus our session tags. */
export type SignalPayload = object;

export interface UseMeetingsHub {
    state: HubConnectionState;
    connectionId: string | null;
    joinMeeting: (meetingId: string) => Promise<void>;
    /** Join (or rejoin after a reconnect) with this room mount's client id. */
    joinMeetingWithSession: (meetingId: string, clientId: string) => Promise<void>;
    /** Anonymous guest join via a shared link — no account/token required. */
    joinMeetingAsGuest: (meetingId: string, displayName: string) => Promise<void>;
    /** Guest join with the guest's stable per-meeting token (same guest across reconnects). */
    joinMeetingAsGuestWithSession: (meetingId: string, displayName: string, guestToken: string, clientId: string) => Promise<void>;
    leaveMeeting: (meetingId: string) => Promise<void>;
    /** Leave the meeting if the room isn't re-mounted within the connection's grace period. */
    leaveMeetingOnRelease: (meetingId: string) => void;
    /** Drop the connection and open a new one (the "Rejoin" button). */
    restart: () => Promise<void>;
    sendOffer: (targetConnectionId: string, offer: SignalPayload) => Promise<void>;
    sendAnswer: (targetConnectionId: string, answer: SignalPayload) => Promise<void>;
    sendIceCandidate: (targetConnectionId: string, candidate: SignalPayload) => Promise<void>;
    toggleMedia: (mediaType: "audio" | "video", enabled: boolean) => Promise<void>;
    raiseHand: (raised: boolean) => Promise<void>;
    /** Host only (enforced by the hub): lower every raised hand. */
    lowerAllHands: () => Promise<void>;
    /** Host only (enforced by the hub): lower one person's hand. */
    lowerHand: (targetConnectionId: string) => Promise<void>;
    screenShare: (sharing: boolean) => Promise<void>;
    /** Screen share with the id of the MediaStream carrying the screen (sent alongside the camera). */
    screenShareStream: (sharing: boolean, streamId: string | null) => Promise<void>;
    sendReaction: (emoji: string) => Promise<void>;
    sendChat: (message: string) => Promise<void>;
    sendCaption: (text: string) => Promise<void>;
    /** "I'm speaking" — the hub rotates speakers into the video set (rate-limited there). */
    reportSpeaking: () => Promise<void>;
    endMeeting: () => Promise<void>;
    // Host controls — broadcasts honoured by clients; host gating on UI.
    setSpotlight: (targetConnectionId: string) => Promise<void>;
    clearSpotlight: () => Promise<void>;
    muteAll: () => Promise<void>;
    /** Host only: lock the meeting so nobody new can join, or unlock it. */
    setMeetingLocked: (locked: boolean) => Promise<void>;
    forceMuteOne: (targetConnectionId: string) => Promise<void>;
    setLayout: (layoutMode: "gallery" | "spotlight" | "focus" | "together") => Promise<void>;
    /** Host only (enforced by the hub): tell everyone the recording started or stopped. */
    setRecordingState: (isRecording: boolean) => Promise<void>;
    /** @deprecated Same as setRecordingState. */
    broadcastRecording: (isRecording: boolean) => Promise<void>;
    // Lobby
    /** Knock on the lobby — sent before the host has admitted this user. */
    knockLobby: (meetingId: string) => Promise<void>;
    /** Host admits a waiting participant by their connectionId. */
    admitFromLobby: (meetingId: string, targetConnectionId: string) => Promise<void>;
    /** Host denies a waiting participant by their connectionId. */
    denyFromLobby: (meetingId: string, targetConnectionId: string) => Promise<void>;
    /** Listen to one hub event from a child component; returns the unsubscribe. */
    on: (name: MeetingsEventName, handler: (payload: unknown) => void) => () => void;
    // Whiteboard (caller must be in the room)
    whiteboardOpen: () => Promise<WhiteboardOpenResult | undefined>;
    whiteboardClose: () => Promise<void>;
    /** JSON array of ops (≤ 50, ≤ 30 KB). */
    whiteboardOps: (opsJson: string) => Promise<WhiteboardOpsResult | undefined>;
    whiteboardPreview: (objJson: string | null) => Promise<void>;
    whiteboardView: (centerX: number, centerY: number, zoom: number) => Promise<void>;
    whiteboardLead: (lead: boolean) => Promise<void>;
    /** Organizer only (enforced by the hub). */
    whiteboardClear: () => Promise<void>;
    /** Organizer only (enforced by the hub): "Only presenters can draw". */
    whiteboardLock: (locked: boolean) => Promise<void>;
    // Meeting notes (signed-in; same access rule as the meeting chat)
    watchNotes: (meetingId: string) => Promise<NoteLock[] | undefined>;
    unwatchNotes: (meetingId: string) => Promise<void>;
    /** False when someone else is already editing that block. */
    notesEditing: (meetingId: string, blockId: string, editing: boolean) => Promise<boolean | undefined>;
}

/**
 * Subscribes the component to the shared `/hubs/meetings` connection. Handlers can change across renders
 * without re-subscribing; the connection itself is never rebuilt because a token rotated (the factory reads
 * the latest token at negotiate time).
 */
export function useMeetingsHub(events: EventMap, opts?: { guest?: boolean }): UseMeetingsHub {
    const token = useAccessToken();
    const guest = opts?.guest ?? false;
    const shared = guest ? guestMeetingsHub : userMeetingsHub;
    const handlersRef = useRef(events);
    handlersRef.current = events;
    const [snapshot, setSnapshot] = useState(shared.snapshot);

    // Guests connect anonymously even when the browser also has a signed-in session.
    useEffect(() => { if (!guest) setHubAccessToken(token); }, [token, guest]);

    useEffect(() => {
        shared.acquire();
        const offs = Object.values(MeetingsEvents).map((name) =>
            shared.on(name, (payload: unknown) => { handlersRef.current[name]?.(payload); }));
        const offState = shared.subscribe(() => setSnapshot(shared.snapshot));
        setSnapshot(shared.snapshot);
        return () => {
            for (const off of offs) off();
            offState();
            shared.release();
        };
    }, [shared]);

    const invoke = useCallback((name: string, ...args: unknown[]) => shared.invoke(name, ...args), [shared]);
    const invokeResult = useCallback(<T,>(name: string, ...args: unknown[]) => shared.invokeResult<T>(name, ...args), [shared]);
    const on = useCallback((name: MeetingsEventName, handler: (payload: unknown) => void) =>
        shared.on(name, handler as (...args: unknown[]) => void), [shared]);

    // MUST be memoized: an unmemoized object literal changes identity every render, so every effect in
    // MeetingRoom that depends on `hub` would re-run continuously — including the join effect.
    return useMemo<UseMeetingsHub>(() => ({
        state: snapshot.state,
        connectionId: snapshot.connectionId,
        joinMeeting: (meetingId) => invoke("JoinMeeting", meetingId),
        joinMeetingWithSession: (meetingId, clientId) =>
            shared.invokeWithFallback("JoinMeetingWithSession", [meetingId, clientId], "JoinMeeting", [meetingId]),
        joinMeetingAsGuest: (meetingId, displayName) => invoke("JoinMeetingAsGuest", meetingId, displayName),
        joinMeetingAsGuestWithSession: (meetingId, displayName, guestToken, clientId) =>
            shared.invokeWithFallback("JoinMeetingAsGuestWithSession", [meetingId, displayName, guestToken, clientId],
                "JoinMeetingAsGuest", [meetingId, displayName]),
        leaveMeeting: (meetingId) => invoke("LeaveMeeting", meetingId),
        leaveMeetingOnRelease: (meetingId) => shared.addFinalizer((c) => c.invoke("LeaveMeeting", meetingId)),
        restart: () => shared.restart(),
        sendOffer: (target, offer) => invoke("SendOffer", target, offer),
        sendAnswer: (target, answer) => invoke("SendAnswer", target, answer),
        sendIceCandidate: (target, candidate) => invoke("SendIceCandidate", target, candidate),
        toggleMedia: (mediaType, enabled) => invoke("ToggleMedia", mediaType, enabled),
        raiseHand: (raised) => invoke("RaiseHand", raised),
        lowerAllHands: () => invoke("LowerAllHands"),
        lowerHand: (target) => invoke("LowerHand", target),
        screenShare: (sharing) => invoke("ScreenShare", sharing),
        screenShareStream: (sharing, streamId) =>
            shared.invokeWithFallback("ScreenShareStream", [sharing, streamId], "ScreenShare", [sharing]),
        sendReaction: (emoji) => invoke("SendReaction", emoji),
        sendChat: (message) => invoke("SendMeetingChat", message),
        sendCaption: (text) => invoke("SendCaption", text),
        reportSpeaking: () => invoke("ReportSpeaking"),
        endMeeting: () => invoke("EndMeeting"),
        setSpotlight: (target) => invoke("SetSpotlight", target),
        clearSpotlight: () => invoke("SetSpotlight", ""),
        muteAll: () => invoke("MuteAll"),
        setMeetingLocked: (locked) => invoke("SetMeetingLocked", locked),
        forceMuteOne: (target) => invoke("ForceMuteOne", target),
        setLayout: (layoutMode) => invoke("SyncLayout", layoutMode),
        setRecordingState: (isRecording) => invoke("SetRecordingState", isRecording),
        broadcastRecording: (isRecording) => invoke("SetRecordingState", isRecording),
        // Lobby
        knockLobby: (meetingId) => invoke("KnockLobby", meetingId),
        admitFromLobby: (meetingId, targetConnectionId) => invoke("AdmitFromLobby", meetingId, targetConnectionId),
        denyFromLobby: (meetingId, targetConnectionId) => invoke("DenyFromLobby", meetingId, targetConnectionId),
        on,
        whiteboardOpen: () => invokeResult<WhiteboardOpenResult | null>("WhiteboardOpen").then((r) => r ?? undefined),
        whiteboardClose: () => invoke("WhiteboardClose"),
        whiteboardOps: (opsJson) => invokeResult<WhiteboardOpsResult>("WhiteboardOps", opsJson),
        whiteboardPreview: (objJson) => invoke("WhiteboardPreview", objJson),
        whiteboardView: (centerX, centerY, zoom) => invoke("WhiteboardView", centerX, centerY, zoom),
        whiteboardLead: (lead) => invoke("WhiteboardLead", lead),
        whiteboardClear: () => invoke("WhiteboardClear"),
        whiteboardLock: (locked) => invoke("WhiteboardLock", locked),
        watchNotes: (meetingId) => invokeResult<NoteLock[]>("WatchNotes", meetingId),
        unwatchNotes: (meetingId) => invoke("UnwatchNotes", meetingId),
        notesEditing: (meetingId, blockId, editing) => invokeResult<boolean>("NotesEditing", meetingId, blockId, editing)
    }), [snapshot.state, snapshot.connectionId, invoke, invokeResult, on, shared]);
}
